<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import kanbanClient from 'dashboard/api/kanban/kanbanClient';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  funnelId: { type: Number, required: true },
  dealId: { type: Number, required: true },
  dirty: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});
const { t } = useI18n();
const { accountId } = useAccount();
const { checkPermissions } = usePolicy();
const allowed = computed(() =>
  checkPermissions(['administrator', 'deal_export'])
);
const pending = ref(false);
const error = ref('');
let controller;

const cancel = () => {
  controller?.abort();
  controller = null;
  pending.value = false;
  error.value = '';
};
watch(
  () => [accountId.value, props.funnelId, props.dealId, allowed.value],
  cancel
);
onBeforeUnmount(cancel);

const download = async () => {
  if (!allowed.value || props.dirty || props.disabled || pending.value) return;
  const request = new AbortController();
  controller = request;
  const dealId = props.dealId;
  pending.value = true;
  error.value = '';
  try {
    const { data } = await kanbanClient.exportDeal(props.funnelId, dealId, {
      signal: request.signal,
    });
    if (controller !== request || request.signal.aborted) return;
    const link = downloadCsvFile(`opportunity-${dealId}.csv`, data);
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  } catch (failure) {
    if (controller !== request || request.signal.aborted) return;
    if (failure.response?.status === 403)
      error.value = t('KANBAN.EXPORT.DENIED');
    else if (failure.response?.status === 404)
      error.value = t('KANBAN.EXPORT.UNAVAILABLE');
    else error.value = t('KANBAN.EXPORT.ERROR');
  } finally {
    if (controller === request) pending.value = false;
  }
};
</script>

<template>
  <div v-if="allowed" class="flex flex-col gap-1">
    <Button
      data-test-id="export-deal"
      type="button"
      variant="ghost"
      color="slate"
      size="sm"
      icon="i-lucide-download"
      :label="t('KANBAN.EXPORT.BUTTON')"
      :title="t('KANBAN.EXPORT.SAVED')"
      :is-loading="pending"
      :disabled="pending || dirty || disabled"
      @click="download"
    />
    <p v-if="dirty" class="m-0 text-xs text-n-slate-11">
      {{ t('KANBAN.EXPORT.UNSAVED') }}
    </p>
    <p v-if="error" role="alert" class="m-0 text-xs text-n-ruby-11">
      {{ error }}
    </p>
  </div>
</template>
